import * as mui from '@mui/material'
import { Field, Form, Formik, FormikValues } from 'formik'
import { TextField } from 'formik-mui'
import React from 'react'
import { Navigate } from 'react-router-dom'
import * as Yup from 'yup'

import { translationsContext } from 'context/translations'
import { Link } from 'components/Link'
import { userContext } from 'context/user'
import { routes } from 'routes'
import { graphqlClient, requireErrorExtensions } from 'utils/graphql'

import logoIcon from '../assets/logo.png'
import { LanguageSelector } from '../LanguageSelector'


export function Login() {
  const t = React.useContext(translationsContext)
  const validationSchema = Yup.object().shape({
    email: Yup.string().email().required(t('validation__required')),
    password: Yup.string().required(t('validation__required')).min(8, t('validation__short_password')),
  })

  const user = React.useContext(userContext)

  const [commonErrors, setCommonErrors] = React.useState<string[]>([])

  const urlParams = new URLSearchParams(window.location.search)
  const registered = urlParams.get('registered')
  const passwordResetSent = urlParams.get('reset_sent')
  const passwordReset = urlParams.get('reset')

  function handleSubmit(
    values: FormikValues,
    {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}
  ) {
    setCommonErrors([])
    graphqlClient.chain.mutation
      .login({email: values.email, password: values.password})
      .execute({__scalar: 1})
      .then(() => user.refetch())
      .catch(e => {
        const {common_errors} = requireErrorExtensions(e)
        // This form renders no field errors, so a GraphQL error without common_errors
        // is nothing we can show the user — let it reach Sentry.
        if (!common_errors) throw e
        setCommonErrors(common_errors)
      })
      .finally(() => setSubmitting(false))
  }

  if (user.data) return <Navigate to={routes.INDEX} />
  
  return (
    <mui.Container component="main" maxWidth="sm">
      <mui.Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={logoIcon} alt="logo" height="100"/>
        <mui.Typography component="h1" variant="h5" mt={4}>
          {t('login__title')}
        </mui.Typography>
        <Formik
          initialValues={{email: '', password: ''}}
          onSubmit={handleSubmit}
          sx={{mt: 1}}
          validateOnBlur={true}
          validateOnChange={false}
          validationSchema={validationSchema}
        >
          {({submitForm, isSubmitting, touched, errors, isValid}) => (
            <Form style={{width: '100%'}}>
              <mui.Stack spacing={3} mt={3}>
                {registered && (
                  <mui.Alert severity="success">
                    {t('login__registration_success_msg_1')}<br/>
                    {t('login__registration_success_msg_2')}<br/>
                  </mui.Alert>
                )}
                {passwordResetSent && (
                  <mui.Alert severity="success">
                    {t('login__reset_sent_msg_1')}<br/>
                    {t('login__reset_sent_msg_2')}<br/>
                  </mui.Alert>
                )}
                {passwordReset && (
                  <mui.Alert severity="success">
                    {t('login__reset_success_msg_1')}<br/>
                    {t('login__reset_success_msg_2')}<br/>
                  </mui.Alert>
                )}
                {commonErrors.map((error, i) => (
                  <mui.Alert key={i} severity="error">
                    {t(`api__${error}`)}
                  </mui.Alert>
                ))}
                <Field
                  component={TextField}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    autoComplete: 'email',
                    fullWidth: true,
                  }}
                  InputLabelProps={{shrink: true}}
                  label={t('login__field__email')}
                  name="email"
                />
                <Field
                  component={TextField}
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    autoComplete: 'current-password',
                    fullWidth: true,
                    type: 'password',
                  }}
                  InputLabelProps={{shrink: true}}
                  label={t('login__field__password')}
                  name="password"
                />
                <mui.Button
                  disabled={isSubmitting || !isValid}
                  fullWidth
                  onClick={submitForm}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    backgroundColor: '#A8C02A',
                    '&:hover': {
                      backgroundColor: '#C1CC00',
                    },
                }}
                  variant="contained"
                >
                  {t('login__button__submit')}
                </mui.Button>
                <mui.Stack direction="row" justifyContent="space-between">
                  <mui.Stack>
                    <Link to={routes.FORGOT_PASSWORD} variant="body2">
                      {t('login__link__forgot_password')}
                    </Link>
                    <Link to={routes.REGISTER} variant="body2">
                      {t('login__link__registration')}
                    </Link>
                  </mui.Stack>
                  <LanguageSelector />
                </mui.Stack>
              </mui.Stack>
            </Form>
          )}
        </Formik>
      </mui.Box>
    </mui.Container>
  )
}